import { useContext, useEffect, useRef, useState } from 'react'

import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import { customerChargesList } from 'api/requests'
import { AuthContext } from 'context/AuthContext'
import { roles } from 'models/User'
import { ParsedUrlQuery } from 'querystring'
import {
  CustomerCharges,
  customerChargesTableMapper
} from 'templates/v2/Charge/components/ChargeTable'
import { NfseStatus } from 'templates/v2/Charge/components/ChargeTable/ChargeTable'

import EmptyState from 'components/EmptyState/EmptyState'
import { Table } from 'components/Table'
import { SelectParams } from 'components/Table/Table.interface'
import { HeadCell } from 'components/Table/Table.interface'
import { TableSkeleton } from 'components/Table/TableSkeleton'

import browserQueryToQueryString from 'utils/query-string'

export interface DataCustomerCharges {
  error: boolean
  records: Record[]
  meta: Meta
  statistics: StatisticsMapperParams
}

export type StatisticsMapperParams = {
  total_billed: number
  total_count: number
  total_paid: number
  total_received: number
  total_to_receive: number
  total_not_due: number
  total_due_up_to_7_days: number
  total_due_more_than_7_days: number
  total_churn: number
  total_due: number
  total_leads: number
}

export interface Record {
  customer: Customer
  due_date: string
  date_paid: string | null
  amount_paid: string
  amount_billed: string
  status: string
  uuid: string
  viewed: boolean
  recurrence_uuid: string
  nfse_status: NfseStatus
  nfse: Nfse
  nfse_policy: string
}

export interface Nfse {
  status: NfseStatus
  uuid: string
}

export interface Customer {
  name: string
  nickname: string
  uuid: string
}

interface Meta {
  current_page: number
  next_page: number
  prev_page: any
  total_pages: number
  total_count: number
}

const tableHeader = [
  { id: 'name', label: 'Cliente' },
  { id: 'dueDate', label: 'Vencimento' },
  { id: 'datePaid', label: 'Pagamento' },
  { id: 'amountBilled', label: 'Valor' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: '' }
] as HeadCell[]

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100]

type ChargeTableProps = {
  baseFilters: { [key in string]: string } | ParsedUrlQuery
  select: SelectParams
  pinnedCharge?: Record
  onRecordsChange?: (records: Record[]) => void
}

const ChargeTable = ({
  baseFilters,
  select,
  pinnedCharge,
  onRecordsChange
}: ChargeTableProps) => {
  const { user } = useContext(AuthContext)
  const AUTHORIZATION = roles[user?.role || 'guest']
  const [loading, setLoading] = useState(true)

  const [customerCharges, setCustomerCharges] = useState({} as CustomerCharges)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0])
  const [totalCount, setTotalCount] = useState(0)

  // A seleção persiste entre páginas, então o pai pode precisar do registro cru
  // (o due_date vive nele) de uma cobrança que não está mais renderizada. Por
  // isso acumulamos o que já foi carregado em vez de trocar a cada página.
  const loadedChargesRef = useRef(new Map<string, Record>())
  // Trocar de filtro enquanto uma página carrega dispara dois fetches; só o
  // último pode escrever na tela.
  const requestIdRef = useRef(0)

  const filterKey = JSON.stringify(baseFilters)
  const [appliedFilterKey, setAppliedFilterKey] = useState(filterKey)

  // Troca de filtro volta pra primeira página e zera o acumulado (o pai também
  // limpa a seleção nesse momento). Ajustar durante o render — e não num effect
  // — evita disparar um fetch da página antiga antes do reset.
  if (appliedFilterKey !== filterKey) {
    setAppliedFilterKey(filterKey)
    setPage(0)
    loadedChargesRef.current = new Map<string, Record>()
  }

  const handleGetCustomerCharges = async () => {
    const requestId = ++requestIdRef.current
    const isStale = () => requestId !== requestIdRef.current

    try {
      setLoading(true)

      const response = await customerChargesList<DataCustomerCharges>({
        filter_by: browserQueryToQueryString({
          ...(baseFilters as { [key in string]: string }),
          // Sem `page`/`per` explícitos a API cai no default do Kaminari (25) e
          // não há como alcançar as cobranças seguintes.
          page: String(page),
          per: String(rowsPerPage)
        })
      })

      if (isStale()) return

      const pageRecords = response.data.records ?? []

      // Página vazia fora da primeira (cobrança apagada em outra aba, filtro que
      // encolheu) deixaria o usuário num beco sem saída: o empty state não tem
      // paginação pra voltar.
      if (page > 0 && !pageRecords.length) {
        setPage(0)
        return
      }
      // A cobrança de origem do atalho de emissão é fixada no topo da primeira
      // página; nas demais ela seria uma linha fora de ordem.
      const records =
        page === 0 &&
        pinnedCharge &&
        !pageRecords.some((r) => r.uuid === pinnedCharge.uuid)
          ? [pinnedCharge, ...pageRecords]
          : pageRecords

      records.forEach((record) =>
        loadedChargesRef.current.set(record.uuid, record)
      )
      // Expõe os registros crus pro pai (o due_date vive aqui e some no mapper).
      onRecordsChange?.(Array.from(loadedChargesRef.current.values()))

      setTotalCount(response.data.meta?.total_count ?? records.length)

      const data = { ...response.data, records }
      const actions = { AUTHORIZATION, notViewActions: true }
      const customerChargeTable = customerChargesTableMapper({ data, actions })

      setCustomerCharges(customerChargeTable)
    } finally {
      if (!isStale()) {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    handleGetCustomerCharges()
  }, [filterKey, page, rowsPerPage])

  const rows = customerCharges.rows
  const selectedCount = select.selected?.length ?? 0

  // Só o primeiro carregamento troca a tabela pelo skeleton: ao paginar, manter
  // as linhas na tela (esmaecidas) evita o footer de paginação sumir e voltar.
  if (loading && !rows) {
    return <TableSkeleton />
  }

  return !rows?.length ? (
    <Box>
      <Typography
        variant="smLight"
        color="#A3A6A2"
        display="flex"
        justifyContent="center"
      >
        Selecione uma ou mais cobranças:
      </Typography>
      <Box pt={3}>
        <EmptyState
          title="Nenhuma cobrança encontrada"
          message="Não há cobranças associadas a este registro."
        />
      </Box>
    </Box>
  ) : (
    <>
      <Typography
        variant="smLight"
        color="#757675"
        display="flex"
        justifyContent="center"
      >
        Selecione uma ou mais cobranças:
      </Typography>
      {selectedCount > 0 && (
        <Typography
          variant="smLight"
          color="#757675"
          display="flex"
          justifyContent="center"
        >
          {selectedCount === 1
            ? '1 cobrança selecionada'
            : `${selectedCount} cobranças selecionadas`}
        </Typography>
      )}
      <Box
        sx={{
          opacity: loading ? 0.5 : 1,
          pointerEvents: loading ? 'none' : 'auto'
        }}
      >
        <Table
          mobileCards
          {...customerCharges}
          header={tableHeader}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          totalCount={totalCount}
          handleChangePage={(_, newPage) => setPage(newPage)}
          handleChangeRowsPerPage={(event) => {
            setRowsPerPage(Number(event.target.value))
            setPage(0)
          }}
          hoverInRow
          disableFilter
          select={{ ...select, persistAcrossPages: true }}
        />
      </Box>
    </>
  )
}

export default ChargeTable
